import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { PIN_LENGTH } from 'utils/constants';
import { calcSizeForDevice } from 'utils/index';

import NewGradientButton from 'components/Button/NewGradienteButton';
import { PinModalProps } from 'components/Pin/PinModal';
import { ProfilePinField } from 'components/Profile/Form/PinField';
import TvKeyboard from 'components/TvKeyboard';
import { WebDiv } from 'components/WebDiv';

export const TvPinModal = ({
  variant = 'verify',
  isOpen,
  error,
  onConfirm,
  onCancel,
}: PinModalProps) => {
  const [pin, setPin] = useState('');

  const isVerify = variant === 'verify';
  const title = isVerify ? 'Insira seu PIN para continuar' : 'Criar PIN da conta';
  const textBolt = isVerify
    ? `Insira seu PIN de ${PIN_LENGTH} dígitos para continuar.`
    : 'Use seu PIN para manter sua conta segura ao acessar ou alterar seus dados.';
  const text = 'Se precisar, você pode defini-lo novamente na edição de perfil.';

  const handlePinConfirm = () => {
    if (pin.length === PIN_LENGTH) onConfirm(pin);
  };

  useEffect(() => {
    if (!isOpen) setPin('');
  }, [isOpen]);

  useEffect(() => {
    if (pin.length === PIN_LENGTH && variant === 'verify') {
      onConfirm(pin);
    }
  }, [pin, variant, onConfirm]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setPin('');
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [error]);

  if (!isOpen) return null;

  return createPortal(
    <WebDiv className="fullscreen-overlay navigation-container navigation-priority">
      <View style={styles.overlay}>
        <View style={styles.container}>
          <Text style={styles.title}>{title}</Text>
          <Text style={[styles.text, { fontWeight: '600' }]}>{textBolt}</Text>
          <Text
            style={[
              styles.text,
              { marginTop: calcSizeForDevice(12), marginBottom: calcSizeForDevice(32) },
            ]}>
            {text}
          </Text>

          <View style={styles.elementContainer}>
            <ProfilePinField value={pin} onChange={setPin} error={error} />
          </View>

          <WebDiv className="navigation-container navigation-priority">
            <View style={styles.elementContainer}>
              <TvKeyboard
                variant="horizontal-numeric"
                appearance="strong"
                textValue={pin}
                setTextValue={setPin}
              />
            </View>
            {isVerify ? (
              <Pressable accessibilityRole="button" style={styles.cancelBtn} onPress={onCancel}>
                <Text style={styles.cancelBtnText}>Cancelar</Text>
              </Pressable>
            ) : (
              <NewGradientButton style={styles.saveBtn} onPress={handlePinConfirm}>
                <Text style={styles.saveBtnText}>Salvar PIN</Text>
              </NewGradientButton>
            )}
          </WebDiv>
        </View>
      </View>
    </WebDiv>,
    document.getElementById('root')!
  ) as React.ReactPortal;
};

const styles = StyleSheet.create({
  overlay: {
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: calcSizeForDevice(829),
    paddingHorizontal: calcSizeForDevice(122),
    paddingVertical: calcSizeForDevice(32),
    backgroundColor: '#050923',
    borderWidth: 2,
    borderRadius: 32,
    borderColor: '#444444',
  },
  title: {
    fontSize: calcSizeForDevice(36),
    fontWeight: '700',
    color: '#7A8CFF',
    textAlign: 'center',
    marginBottom: calcSizeForDevice(32),
  },
  text: {
    fontSize: calcSizeForDevice(20),
    fontWeight: '400',
    color: '#FFF',
    textAlign: 'center',
  },
  elementContainer: {
    alignSelf: 'center',
    marginBottom: calcSizeForDevice(32),
  },
  saveBtn: {
    height: calcSizeForDevice(54),
  },
  saveBtnText: {
    fontSize: calcSizeForDevice(20),
    fontWeight: '600',
  },
  cancelBtn: {
    width: '100%',
    height: calcSizeForDevice(54),
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelBtnText: {
    fontSize: calcSizeForDevice(20),
    fontWeight: '600',
    color: '#FFF',
  },
});
